import React, { useRef } from "react";
import {
  StyledActionButtonWrapper,
  StyledAddressActionsBox,
  StyledAddressFieldGrid,
  StyledAddressFormBox,
  StyledProfileAddressFields,
  StyledAddressHeaderBox,
  StyledBoxDefault,
  StyledError,
  StyledHomeDefaultLabelBox,
  StyledInnerBox,
  StyledOuterBox,
  StyledSetDefaultText,
  StyledTypography,
  StyledTypographyDefault,
} from "./style";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { FieldArray, Form, Formik } from "formik";
import Grid from "@mui/material/Grid2";
import CustomInput from "../common/input/CustomInput";
import * as Yup from "yup";
import CustomButton from "../common/button/CustomButton";
import theme from "../../theme";
import { UPDATE_ADDRESS_MULTIPLE } from "../../actions/AddressActions";
import { useDispatch, useSelector } from "react-redux";
import { UPDATE_PROFILE_DETAILS } from "../../actions/AuthActions";
import toast from "react-hot-toast";
import { useAddressLocationData } from "../../hooks/useAddressLocationData";
import CountryStateFields from "../common/country-state-fields/CountryStateFields";
import { DEFAULT_ADDRESS_COUNTRY } from "../../utils/addressLocationOptions";

const AddressDefault = ({ setViewProfile, handleOpen }) => {
  const {
    countries,
    loadingCountries,
    getStatesForCountry,
    getCitiesForCountryState,
  } = useAddressLocationData();

  const validationSchema = Yup.object().shape({
    addresses: Yup.array()
      .of(
        Yup.object().shape({
          street_address: Yup.string().required("Street Address is required"),
          city: Yup.string().required("City is required"),
          state: Yup.string().required("State is required"),
          country: Yup.string().required("Country is required"),
          zip_code: Yup.string().required("Zip code is required"),
        })
      )
      .test(
        "unique-address",
        "Duplicate address entries are not allowed",
        (addresses = []) => {
          const seen = new Set();
          for (const addr of addresses) {
            const key = addr.street_address?.toLowerCase().trim();
            if (seen.has(key)) return false;
            seen.add(key);
          }
          return true;
        }
      ),
  });
  const addressList = useSelector((state) => state?.address?.addressList);

  const isProfileCompleted = useSelector(
    (state) => state.auth?.profileDetails?.is_profile_completed
  );
  const isPersonalShopper =
    useSelector((state) => state.auth?.profileDetails?.user_type) ===
    "personal_shopper";

  const dispatch = useDispatch();
  const submitLock = useRef(false);
  const isSubmitting = useSelector((state) => state?.address?.isLoading);

  const handleAddressSubmit = (values) => {
    if (submitLock.current || isSubmitting) return;

    const filledAddresses = (values?.addresses ?? []).filter(
      (addr) =>
        String(addr?.street_address ?? "").trim() ||
        String(addr?.city ?? "").trim() ||
        String(addr?.state ?? "").trim() ||
        String(addr?.zip_code ?? "").trim()
    );

    if (isPersonalShopper && filledAddresses.length === 0) {
      setViewProfile(false);
      return;
    }

    if (values?.addresses?.length > 5) {
      toast.error("You are not allowed to add more than 5 address");
      return;
    }

    submitLock.current = true;

    dispatch({
      type: UPDATE_ADDRESS_MULTIPLE,
      payload: {
        ...values,
        addresses: filledAddresses,
        onSuccess: () => {
          if (!isPersonalShopper) {
            dispatch({
              type: UPDATE_PROFILE_DETAILS,
              payload: { is_profile_completed: true },
            });
          }
          setViewProfile(false);
          if (!isProfileCompleted && !isPersonalShopper) {
            handleOpen();
          }
        },
        onError: () => {
          submitLock.current = false;
        },
      },
    });
  };

  return (
    <>
      <Formik
        initialValues={{
          addresses: addressList?.length
            ? addressList
            : isPersonalShopper
              ? []
              : [
                {
                  street_address: "",
                  city: "",
                  state: "",
                  country: DEFAULT_ADDRESS_COUNTRY,
                  zip_code: "",
                  is_default: true,
                  address_type: "work",
                },
                {
                  street_address: "",
                  city: "",
                  state: "",
                  country: DEFAULT_ADDRESS_COUNTRY,
                  zip_code: "",
                  is_default: false,
                  address_type: "home",
                },
              ],
          delete_address_ids: [],
        }}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleAddressSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          errors,
          touched,
          setFieldValue,
        }) => (
          <Form>
            <FieldArray name="addresses">
              {({ push, remove }) => (
                <>
                  {values.addresses.map((address, index) => {
                    return (
                      <StyledBoxDefault
                        key={index}
                        isdefault={address?.is_default ? "true" : "false"}
                      >
                        <StyledAddressHeaderBox>
                          {typeof errors.addresses === "string" && (
                            <StyledError>{errors.addresses}</StyledError>
                          )}
                          {
                            <StyledHomeDefaultLabelBox>
                            {/* Work/Home Toggle */}
                            <div style={{
                              display: "flex",
                              background: "#EAEAEA",
                              borderRadius: "100px",
                              padding: "3px",
                              gap: "2px",
                            }}>
                              {["home", "work"].map((type) => (
                                <StyledTypography
                                  key={type}
                                  style={{
                                    cursor: "pointer",
                                    background: address.address_type === type
                                      ? theme.palette.custom.primary
                                      : "transparent",
                                    color: address.address_type === type
                                      ? theme.palette.custom.white
                                      : theme.palette.custom.lockerValueColor,
                                    fontWeight: address.address_type === type
                                      ? theme.typography.fontWeight.medium
                                      : theme.typography.fontWeight.regular,
                                    transition: "all 0.2s ease",
                                    width: "80px",
                                  }}
                                  onClick={() => setFieldValue(`addresses[${index}].address_type`, type)}
                                >
                                  {type.charAt(0).toUpperCase() + type.slice(1)}
                                </StyledTypography>
                              ))}
                            </div>
                          
                            {/* Default Label */}
                            {address?.is_default && (
                              <Tooltip title="Default" arrow>
                                <StyledTypographyDefault>
                                  <StarRoundedIcon fontSize="small" />
                                </StyledTypographyDefault>
                              </Tooltip>
                            )}
                          </StyledHomeDefaultLabelBox>
                          
                          }

                          <StyledAddressActionsBox>
                            {values.addresses[index].is_default === false && (
                              <StyledSetDefaultText
                                onClick={() => {
                                  setFieldValue(
                                    "addresses",
                                    values.addresses.map((val, ind) => {
                                      if (ind === index) {
                                        return {
                                          ...val,
                                          is_default: true,
                                        };
                                      } else {
                                        return {
                                          ...val,
                                          is_default: false,
                                        };
                                      }
                                    })
                                  );
                                }}
                              >
                                Set as Default
                              </StyledSetDefaultText>
                            )}

                            <Box
                              onClick={() => {
                                if (address?.id) {
                                  setFieldValue("delete_address_ids", [
                                    ...values.delete_address_ids,
                                    address.id,
                                  ]);
                                }
                                remove(index);
                              }}
                              style={{
                                cursor: "pointer",
                                border: "1px solid #FF3850",
                                borderRadius: "8px",
                                width: "36px",
                                height: "36px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <DeleteOutlineRoundedIcon style={{ color: "#FF3850", fontSize: "20px" }} />
                            </Box>
                          </StyledAddressActionsBox>
                        </StyledAddressHeaderBox>
                        <StyledAddressFormBox>
                          <StyledProfileAddressFields>
                          <Grid container columnSpacing={2} rowSpacing={1.5}>
                            <StyledAddressFieldGrid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                              <CustomInput
                                profileForm
                                required
                                customLabel="Street Address"
                                name={`addresses[${index}].street_address`}
                                value={address.street_address}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={
                                  touched.addresses?.[index]?.street_address &&
                                  Boolean(
                                    errors.addresses?.[index]?.street_address
                                  )
                                }
                                helperText={
                                  touched.addresses?.[index]?.street_address &&
                                  errors.addresses?.[index]?.street_address
                                }
                              />
                            </StyledAddressFieldGrid>

                            <CountryStateFields
                              index={index}
                              hideCountry={false}
                              countries={countries}
                              loadingCountries={loadingCountries}
                              getStatesForCountry={getStatesForCountry}
                              getCitiesForCountryState={getCitiesForCountryState}
                              values={values}
                              errors={errors}
                              touched={touched}
                              setFieldValue={setFieldValue}
                              handleBlur={handleBlur}
                              showZip
                              zipPath={`addresses[${index}].zip_code`}
                              muiGridRow
                            />
                          </Grid>
                          </StyledProfileAddressFields>
                        </StyledAddressFormBox>
                      </StyledBoxDefault>
                    );
                  })}
                  <StyledOuterBox>
                    <StyledInnerBox>
                      <CustomButton
                        btnLabel="Add Address"
                        color={theme.palette.custom.white}
                        onClick={() =>
                          push({
                            street_address: "",
                            city: "",
                            state: "",
                            country: DEFAULT_ADDRESS_COUNTRY,
                            zip_code: "",
                            is_default: false,
                            address_type: "home",
                          })
                        }
                      />
                    </StyledInnerBox>
                  </StyledOuterBox>
                </>
              )}
            </FieldArray>

            <StyledActionButtonWrapper>
              <CustomButton
                isprofile="true"
                fontSize={theme.typography.base.fontSize}
                height="54px"
                btnLabel="Cancel"
                btntype={"white"}
                color={theme.palette.custom.lockerValueColor}
                fontWeight={theme.typography.fontWeight.medium}
                border={theme.borders.defaultButton}
                onClick={() => setViewProfile(false)}
              />

              <CustomButton
                isprofile="true"
                fontSize={theme.typography.base.fontSize}
                height="54px"
                type="submit"
                btnLabel="Save changes"
                btntype={"orderCards"}
                color={theme.palette.custom.white}
                fontWeight={theme.typography.fontWeight.medium}
                border={theme.borders.defaultButton}
                disabled={isSubmitting}
                isLoading={isSubmitting}
              />
            </StyledActionButtonWrapper>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddressDefault;
